// Day Care module API client — same transport rules as src/pages/ot/api/otApi.js:
//
//   GET  -> `res.data.data` is the payload. `get()` unwraps it and throws an
//        Error carrying `.body` when the server says Failed.
//   POST/PATCH/PUT/DELETE -> the whole envelope, normalised to { ok, message, data }.
//
// Business failures arrive as HTTP 200 with status "Failed" and is_valid: true,
// so they are returned (ok === false), never thrown: a 2xx body with
// is_valid:false would trip the global interceptor's logout guard.
// Only transport and network failures reject a read.

import axios from "axios";

export const BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL ||
  "https://manage-dev.hlthtek.com/app/api/";

function authHeaders(extra = {}) {
  const token = sessionStorage.getItem("accessToken") || "";
  return {
    Authorization: `Bearer ${token}`,
    "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
    "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
    "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    USERNAME: sessionStorage.getItem("userName") || "",
    ...extra,
  };
}

function url(path, params) {
  const query = params
    ? Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&")
    : "";
  return `${BASE}daycare/${path}${query ? `?${query}` : ""}`;
}

/** Normalise any Day Care response into { ok, message, data, ...extras }. */
function envelope(body) {
  const payload = body || {};
  return {
    ...payload,
    ok: payload.status === "Success",
    message: payload.message || "",
    data: payload.data,
  };
}

async function get(path, params) {
  const res = await axios.get(url(path, params), { headers: authHeaders() });
  const body = envelope(res?.data);
  if (!body.ok) {
    const err = new Error(body.message || "Could not load");
    err.body = body;
    throw err;
  }
  return body.data;
}

async function send(method, path, body) {
  try {
    const res = await axios({
      method,
      url: url(path),
      data: body,
      headers: authHeaders({ "Content-Type": "application/json" }),
    });
    return envelope(res?.data);
  } catch (err) {
    return {
      ok: false,
      message: err.response?.data?.message || err.message || "Network or server communication failure",
      data: null,
      error: err,
    };
  }
}

const post = (path, body) => send("post", path, body);
const patch = (path, body) => send("patch", path, body);
const put = (path, body) => send("put", path, body);
const del = (path, body) => send("delete", path, body);

// ------------------------------------------------------------------ setup
/**
 * IPD services of the master, for a unit's service list. A day-care session is
 * billed on an IPD admission, so only IPD-episode rows are offered. Not a
 * daycare/ route: it lives on the main EMR surface. Rows carry
 * medical_service_id (the master UUID the unit stores) and term (the name).
 */
export async function searchIpdServices(name) {
  const query = `name=${encodeURIComponent(name || "")}&episode_type=IPD&limit=25`;
  try {
    const res = await axios.get(`${BASE}search_medical_services/?${query}`, { headers: authHeaders() });
    const rows = res?.data?.data;
    return Array.isArray(rows) ? rows : [];
  } catch (err) {
    // A picker that cannot reach the master must not break the form it sits in.
    return [];
  }
}

export const fetchUnits = (params) => get("units/", params);
export const createUnit = (body) => post("units/", body);
export const updateUnit = (id, body) => patch(`units/${id}/`, body);
export const saveMachine = (unitId, body) => post(`units/${unitId}/machines/`, body);

// ----------------------------------------------------------------- orders
export const fetchOrders = (params) => get("orders/", params);
export const createOrder = (body) => post("orders/", body);
export const stopOrder = (id, reason) => patch(`orders/${id}/`, { action: "stop", reason });
export const changeOrder = (id, body) => patch(`orders/${id}/`, { ...body, action: "change" });
export const fetchProposal = (orderId, shiftCode) =>
  get(`orders/${orderId}/proposal/`, { shift_code: shiftCode });

// --------------------------------------------------------------- sessions
export const fetchDay = (unitId, date) => get("sessions/", { unit_id: unitId, date });
export const bookSession = (body) => post("sessions/", body);
export const cancelSession = (id, reason) => patch(`sessions/${id}/`, { action: "cancel", reason });
export const markDna = (id) => patch(`sessions/${id}/`, { action: "dna" });
export const moveSession = (id, body) => patch(`sessions/${id}/`, { ...body, action: "move" });
export const createBlock = (body) => post("blocks/", body);
export const deleteBlock = (id) => del(`blocks/${id}/`);

// -------------------------------------------------------- admission bridge
export const releaseSession = (id) => post(`sessions/${id}/release/`, {});
export const fetchCheckinTemplate = (id) => get(`sessions/${id}/checkin/`);
export const linkAdmission = (id, admissionId) =>
  post(`sessions/${id}/link_admission/`, { admission_id: admissionId });
export const markConverted = (id) => post(`sessions/${id}/converted/`, {});

// ------------------------------------------------------------------ chart
export const fetchChart = (id) => get(`sessions/${id}/chart/`);
export const saveChart = (id, body) => put(`sessions/${id}/chart/`, body);
